// Prospection de partenaires (D-111) : le club contacte, Arceau prépare. Ici, sans base ni écran : les étapes du
// suivi, les dates de « Plus tard », le rappel, « Qui connaît ? » (en rotation, D-118), les chiffres de la plaquette et les argumentaires
// écrits à l'avance par secteur (aucun texte généré, aucune IA). Vouvoiement : tout ce qui part vers une entreprise.
import { ofClub } from './derogation';
import { familyOf, type Family } from './registre';

export type Stage = 'a_contacter' | 'contacte' | 'rendez_vous' | 'proposition' | 'signe' | 'plus_tard' | 'non';
/** Le chemin d'une fiche, puis les trois issues. */
export const FUNNEL: Stage[] = ['a_contacter', 'contacte', 'rendez_vous', 'proposition'];
export const PATH: Stage[] = [...FUNNEL, 'signe'];
export const ENDS: Stage[] = ['signe', 'plus_tard', 'non'];
export const STAGE_LABEL: Record<Stage, string> = {
  a_contacter: 'À contacter', contacte: 'Contacté', rendez_vous: 'Rendez-vous', proposition: 'Proposition', signe: 'Signé', plus_tard: 'Plus tard', non: 'Non',
};
export const STAGE_CHIP: Record<Stage, string> = {
  a_contacter: 'pz-st-todo', contacte: 'pz-st-ct', rendez_vous: 'pz-st-rdv', proposition: 'pz-st-prop', signe: 'pz-st-ok', plus_tard: 'pz-st-later', non: 'pz-st-no',
};
export const isStage = (s: unknown): s is Stage => typeof s === 'string' && s in STAGE_LABEL;

export interface StageCounts { [k: string]: number }
/** Les compteurs du suivi : une fiche par étape. */
export function stageCounts(list: { stage: Stage | null }[]): Record<Stage, number> {
  const out = Object.fromEntries(Object.keys(STAGE_LABEL).map(k => [k, 0])) as Record<Stage, number>;
  for (const c of list) if (c.stage) out[c.stage]++;
  return out;
}

// --- Dates --------------------------------------------------------------------------------------------
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const at = (s: string) => new Date(`${s}T12:00:00Z`);
const addDays = (s: string, n: number) => { const d = at(s); d.setUTCDate(d.getUTCDate() + n); return ymd(d); };
/** Le premier lundi à partir de cette date (incluse). */
export function mondayFrom(s: string): string { const dow = at(s).getUTCDay(); return addDays(s, (8 - dow) % 7); }
export const daysBetween = (a: string, b: string) => Math.round((at(b).getTime() - at(a).getTime()) / 86_400_000);

/** « Plus tard » : fin août (le lundi à partir du 20 août, avant la rentrée et les budgets), janvier (le lundi à partir
 *  du 4, après les fêtes), dans 6 mois (même jour, ou le dernier du mois). Toujours dans le futur. */
export function laterDates(today: string): { fin_aout: string; janvier: string; six_mois: string } {
  const y = Number(today.slice(0, 4));
  const aout = mondayFrom(`${y}-08-20`);
  const jan = mondayFrom(`${y}-01-04`);
  const d = at(today), m = d.getUTCMonth() + 6, yy = d.getUTCFullYear() + Math.floor(m / 12), mm = m % 12;
  const last = new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
  return {
    fin_aout: aout > today ? aout : mondayFrom(`${y + 1}-08-20`),
    janvier: jan > today ? jan : mondayFrom(`${y + 1}-01-04`),
    six_mois: `${yy}-${pad(mm + 1)}-${pad(Math.min(d.getUTCDate(), last))}`,
  };
}
export type LaterKey = 'fin_aout' | 'janvier' | 'six_mois' | 'date';
export const LATER_LABEL: Record<LaterKey, string> = { fin_aout: 'Fin août', janvier: 'Janvier', six_mois: 'Dans 6 mois', date: 'Une date…' };

/** « aujourd'hui », « demain », « dans 5 jours », « en retard de 2 jours ». */
export function dueLabel(today: string, on: string): string {
  const n = daysBetween(today, on);
  if (n === 0) return 'aujourd’hui';
  if (n === 1) return 'demain';
  if (n > 1) return `dans ${n} jours`;
  return n === -1 ? 'en retard d’un jour' : `en retard de ${-n} jours`;
}
/** « 18h00 », « 9h30 » ; « 18:00:00 » de la base compris. */
export const hourLabel = (t: string | null | undefined) => (t ? `${Number(t.slice(0, 2))}h${t.slice(3, 5)}` : '');

/** Ce qu'on note après « C'est fait », d'après le libellé de l'action. */
export type NoteKind = 'visite' | 'appel' | 'email' | 'note';
export const NOTE_LABEL: Record<NoteKind, string> = { visite: 'Visite', appel: 'Appel', email: 'E-mail envoyé par le club', note: 'Note' };
export function kindGuess(label: string | null | undefined): NoteKind {
  const s = (label ?? '').toLowerCase();
  if (/rendez|visite|passer|atelier|rencontr|boutique|magasin/.test(s)) return 'visite';
  if (/appel|rappel|téléphon|telephon/.test(s)) return 'appel';
  if (/mail|courriel|écrire|ecrire|envoy/.test(s)) return 'email';
  return 'note';
}

// --- « Qui connaît ? » en rotation (D-118) ---------------------------------------------------------------
// Chaque lundi, chaque adulte du club : 3 entreprises « à contacter », une à la fois ; 👍 « Je peux présenter »,
// 👎 « Je ne connais personne ». La répartition est faite par la base (_pz_rot_pick) ; ici, les mots.
export const ROT_PER_WEEK = 3;
/** La carte : en cours, les 3 traitées, « Pas cette semaine », rien à proposer. */
export type RotState = 'open' | 'done' | 'closed' | 'empty';
/** Les gestes du bureau sur la fiche : en tête, à sa place, plus jamais proposée. */
export type RotMode = 'prioritaire' | 'normal' | 'retiree';
/** Les compteurs d'une fiche : vue par combien de membres, combien de 👍 et de 👎. */
export interface RotCounts { id: string; seen: number; up: number; down: number }
export type RotStatus = 'prioritaire' | 'rotation' | 'retiree' | 'presentable' | 'hors';
/** Où en est une entreprise dans la rotation : quelqu'un peut la présenter (un 👍 la retire pour tous), hors du suivi
 *  « à contacter », retirée par le bureau, prioritaire, ou à sa place. */
export function rotStatus(c: { stage: Stage | null; rot_priority_at: string | null; rot_out_at: string | null }, up: number): RotStatus {
  if (up > 0) return 'presentable';
  if (c.stage !== 'a_contacter') return 'hors';
  if (c.rot_out_at) return 'retiree';
  return c.rot_priority_at ? 'prioritaire' : 'rotation';
}
export const ROT_STATUS_LABEL: Record<RotStatus, string> = {
  prioritaire: 'Prioritaire', rotation: 'En rotation', retiree: 'Retirée', presentable: 'Quelqu’un peut présenter', hors: 'Hors rotation',
};
/** « Vue par 14 membres · 1 👍 · 13 👎 » ; « Pas encore montrée aux membres ». */
export function seenLine(n: { seen: number; up: number; down: number } | null | undefined): string {
  if (!n || n.seen <= 0) return 'Pas encore montrée aux membres';
  return `Vue par ${n.seen} ${n.seen > 1 ? 'membres' : 'membre'} · ${n.up} 👍 · ${n.down} 👎`;
}
/** Les points de la carte (« ● ● ○ ») : traitées, en cours, à venir. */
export function rotDots(index: number, total: number): ('done' | 'on' | 'todo')[] {
  return Array.from({ length: Math.max(0, total) }, (_, i) => (i + 1 < index ? 'done' : i + 1 === index ? 'on' : 'todo'));
}

// --- Les chiffres du club (plaquette, argumentaires) ---------------------------------------------------
export interface PitchFigure { value: number | null; arceau: number | null; corrected_on: string | null }
export interface PitchFigures {
  season: string | null;
  members: PitchFigure; families: PitchFigure; home_matches: PitchFigure; subscribers: PitchFigure; visits: PitchFigure;
}
export type FigureKey = 'members' | 'families' | 'home_matches' | 'subscribers' | 'visits';
export const FIGURE_LABEL: Record<FigureKey, [string, string]> = {
  members: ['licencié', 'licenciés'], families: ['famille', 'familles'], home_matches: ['match à domicile', 'matchs à domicile'],
  subscribers: ['abonné aux résultats', 'abonnés aux résultats'], visits: ['visite de la page par mois', 'visites de la page par mois'],
};
export const fr = (n: number) => n.toLocaleString('fr-FR').replace(/ /g, ' ');
/** Les visites ne parlent qu'à partir de 100 par mois (comme sur la page partenaires, D-096). */
export const figureShown = (k: FigureKey, v: number | null | undefined): v is number => typeof v === 'number' && (k === 'visits' ? v >= 100 : v > 0);
/** Les cases de la plaquette : jamais les « Je viens voir » (D-111), rien de nul. */
export function plaquetteFigures(f: PitchFigures): { key: FigureKey; value: string; label: string }[] {
  const keys: FigureKey[] = ['members', 'families', 'home_matches', 'subscribers', 'visits'];
  return keys.filter(k => figureShown(k, f[k].value)).map(k => {
    const v = f[k].value!;
    return { key: k, value: fr(v), label: FIGURE_LABEL[k][v > 1 ? 1 : 0] };
  });
}
/** « Chiffres d'Arceau, corrigés par le bureau (licenciés : 142, saisi par le bureau). » */
export function figuresNote(f: PitchFigures): string {
  const fixed = (['members', 'families', 'home_matches', 'subscribers', 'visits'] as FigureKey[]).filter(k => f[k].corrected_on && f[k].value != null);
  const tail = 'Les « Je viens voir » ne sont pas comptés.';
  if (!fixed.length) return `Chiffres calculés par Arceau d’après la saison${f.season ? ` ${f.season}` : ''}. ${tail}`;
  return `Chiffres d’Arceau, corrigés par le bureau (${fixed.map(k => `${FIGURE_LABEL[k][1]} : ${fr(f[k].value!)}`).join(', ')}, saisi par le bureau). ${tail}`;
}

// --- L'argumentaire par secteur (modèles écrits à l'avance) -------------------------------------------------
export type PitchFamily = Exclude<Family, 'autres'>;
export const PITCH_FAMILIES: PitchFamily[] = ['batiment', 'commerce', 'sante', 'auto', 'services', 'immobilier'];
export const PITCH_LABEL: Record<PitchFamily, string> = {
  batiment: 'Artisans du bâtiment', commerce: 'Commerce de proximité', sante: 'Santé', auto: 'Auto et transport', services: 'Services', immobilier: 'Immobilier',
};
export const pitchFamilyOf = (naf: string | null | undefined): PitchFamily => { const f = familyOf(naf); return f === 'autres' ? 'services' : f; };

/** « votre atelier », « votre cabinet »… d'après l'activité. */
export function placeWord(naf: string | null | undefined): string {
  if (naf === '85.53Z') return 'votre auto-école';
  if (naf && /^(49|52|53)/.test(naf)) return 'vos locaux';
  if (naf && /^45/.test(naf)) return 'votre garage';
  if (naf && /^(47\.73|47\.78)/.test(naf)) return 'votre magasin';
  if (naf && /^(10\.71|47\.24)/.test(naf)) return 'votre boulangerie';
  if (naf && /^(56|55)/.test(naf)) return 'votre établissement';
  return ({ batiment: 'votre atelier', commerce: 'votre commerce', sante: 'votre cabinet', auto: 'votre garage', services: 'vos bureaux', immobilier: 'votre agence' } as const)[pitchFamilyOf(naf)];
}

/** « Bonjour Monsieur Cazals, » d'après le contact ajouté par le club ; sinon « Bonjour Madame, Monsieur, ». */
export function greeting(name: string | null | undefined): string {
  const n = (name ?? '').trim().replace(/\s+/g, ' ');
  if (!n) return 'Madame, Monsieur';
  const m = /^(m\.|mr\.?|monsieur|mme\.?|madame|mlle\.?|mademoiselle)\s+(.+)$/i.exec(n);
  if (m) return `${/^m(\.|r\.?|onsieur)$/i.test(m[1]!) ? 'Monsieur' : 'Madame'} ${m[2]}`;
  return n;
}

export interface PitchInput {
  family: PitchFamily;
  club: string; company: string; contact: string | null;
  sender: { first: string | null; title: string | null };
  naf: string | null; distance_m: number | null;
  members: number | null; families: number | null; visits: number | null; home_matches: number | null;
  url: string | null;
}
/** Un morceau de texte : à garder tel quel, ou une valeur reprise de la fiche (surlignée à l'écran). */
export type Piece = string | { v: string };
export interface Pitch { subject: Piece[]; paragraphs: Piece[][] }

const v = (s: string): Piece => ({ v: s });
/** « Le Basket Club de Rivebelle », « L’AS Valmorin » (comme ofClub : « du … », « de l’… »). */
export const theClub = (name: string) => { const n = name.trim(); return /^[aeiouyhàâéèêëîïôöûü]/i.test(n) ? `L’${n}` : `Le ${n}`; };
/** « à l’atelier », « au garage », « dans vos locaux »… */
export function atPlace(naf: string | null | undefined): string {
  const w = placeWord(naf);
  const map: Record<string, string> = {
    'votre atelier': 'à l’atelier', 'votre garage': 'au garage', 'votre cabinet': 'au cabinet', 'votre agence': 'à l’agence', 'votre magasin': 'au magasin',
    'votre boulangerie': 'à la boulangerie', 'votre établissement': 'sur place', 'votre auto-école': 'à l’auto-école', 'vos locaux': 'dans vos locaux',
    'vos bureaux': 'à vos bureaux', 'votre commerce': 'au magasin',
  };
  return map[w] ?? 'sur place';
}
const km = (m: number | null) => (m == null ? null : `${(Math.round(m / 100) / 10).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`);

/** Le modèle du secteur, rempli avec la fiche et les chiffres ; une valeur inconnue retire sa phrase, rien n'est inventé. */
export function fillPitch(p: PitchInput): Pitch {
  const club = p.club;
  const de = ofClub(club);                                    // « du Basket Club de Rivebelle »
  const me = p.sender.first?.trim() || null;
  const who: Piece[] = me ? [v(me), `, ${p.sender.title?.trim() || 'membre du bureau'} ${de}`] : [`Je fais partie du bureau ${de}`];
  const dist = km(p.distance_m);
  const near: Piece[] = dist ? [', à ', v(dist), ` de ${placeWord(p.naf)}`] : [', tout près de chez vous'];
  const people: Piece[] = p.members && p.members > 0
    ? [' Le club fait jouer ', v(`${fr(p.members)} licenciés`), ...(p.families && p.families > 0 ? [' et réunit ', v(`${fr(p.families)} familles`), ' du quartier'] : [])]
    : [' Le club réunit chaque semaine des familles du quartier'];
  const visits: Piece[] = p.visits != null && p.visits >= 100 ? [' (', v(`${fr(p.visits)} visites par mois`), ')'] : [];
  const matches: Piece[] = p.home_matches && p.home_matches > 0 ? [v(`${fr(p.home_matches)} matchs à domicile`), ' cette saison'] : ['nos matchs à domicile'];
  const link: Piece[][] = p.url ? [['Nos formules sont en ligne : ', v(p.url)]] : [];
  const sign: Piece[] = ['Bien à vous,\n', ...(me ? [v(me), ' · '] : []), club];
  const hello: Piece[] = ['Bonjour ', v(greeting(p.contact)), ','];
  const intro: Piece[] = me ? ['Je suis ', ...who, ...near, '.'] : [...who, ...near, '.'];
  const body: Record<PitchFamily, Piece[][]> = {
    batiment: [
      [...intro, ...people, ', qui construisent, rénovent et se recommandent leurs artisans.'],
      ['Vos chantiers se gagnent par le bouche-à-oreille : être vu chaque week-end au gymnase, sur la page du club', ...visits,
        ' et dans l’app des familles, c’est l’entretenir au plus près de chez vous.'],
      [`Je vous propose de passer dix minutes ${atPlace(p.naf)} pour vous montrer ce que cela donnerait : votre logo sur un panneau ou sur un maillot.`],
    ],
    commerce: [
      [...intro, ...people, ', qui font leurs courses à deux pas.'],
      ['Les familles du club passent devant chez vous chaque semaine : votre nom au gymnase, sur la page du club', ...visits,
        ' et une offre réservée aux licenciés dans l’app, c’est une raison de plus de pousser votre porte.'],
      ['Je peux passer vous montrer en cinq minutes ce que le club propose, aux heures qui vous arrangent.'],
    ],
    sante: [
      [...intro, ...people, '.'],
      ['Le club fait bouger les enfants comme les adultes, et prend soin de leur santé : soutenir le club, c’est soutenir le sport près de chez soi. Votre nom figurerait parmi nos partenaires, au gymnase et sur la page du club', ...visits, ', dans le respect des règles de votre profession.'],
      ['Seriez-vous d’accord pour que je passe vous présenter le club quelques minutes ?'],
    ],
    auto: [
      [...intro, ...people, ', qui roulent chaque week-end pour les matchs.'],
      ['Entre les entraînements et ', ...matches, ', les familles du club comptent sur leurs voitures : votre nom au gymnase, sur la page du club', ...visits,
        ' et dans l’app, c’est être leur garage, leur auto-école ou leur transporteur de référence.'],
      ['Je vous propose de passer vous montrer ce que cela donnerait : votre logo sur un panneau ou un « match présenté par » votre entreprise.'],
    ],
    services: [
      [...intro, ...people, '.'],
      ['Associer votre nom à un club de quartier, c’est être connu des familles qui y vivent : au gymnase, sur la page du club', ...visits,
        ' et dans l’app des familles, toute la saison.'],
      ['Auriez-vous un quart d’heure pour que je vous présente nos formules ?'],
    ],
    immobilier: [
      [...intro, ...people, ', qui achètent, vendent et louent autour de vous.'],
      ['Votre agence vit de la confiance du quartier : votre logo au gymnase, sur la page du club', ...visits,
        ' et dans l’app des familles, c’est être la première agence à laquelle elles pensent.'],
      [`Je vous propose de passer dix minutes ${atPlace(p.naf)} pour vous montrer ce que cela donnerait : un panneau au gymnase, un maillot, un « match présenté par ».`],
    ],
  };
  return {
    subject: [`${theClub(club)} et `, v(p.company)],
    paragraphs: [hello, ...body[p.family], ...link, sign],
  };
}

/** Le texte à copier ou à ouvrir dans la messagerie. */
export const pieceText = (ps: Piece[]) => ps.map(x => (typeof x === 'string' ? x : x.v)).join('');
export function pitchText(p: Pitch): { subject: string; body: string } {
  return { subject: pieceText(p.subject), body: p.paragraphs.map(pieceText).join('\n\n') };
}
/** Le lien « Ouvrir dans ma messagerie » : rien ne part d'Arceau, c'est l'appareil qui envoie. */
export function mailtoHref(to: string | null, p: Pitch): string {
  const { subject, body } = pitchText(p);
  return `mailto:${to ? encodeURIComponent(to) : ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
